import { useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { Card } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { FormSelect } from '../FormSelect';
import { LoadingButton } from '../LoadingButton';
import { ISelectOption } from '../Select/types';
import { AllowedFileTypesEnum } from '../../../legacy/shared-types';
import { IDocumentUploadProps } from './types';

const BYTES_IN_MB = 1000000;
const DEFAULT_MAX_FILE_SIZE_IN_MB = 12;

interface IUploadFileForm {
  documentType: string | null;
}

export const DocumentUpload = ({
  documentTypeOptions,
  isUploading,
  isDisabled = false,
  onUpload,
  maxFileSizeInMB = DEFAULT_MAX_FILE_SIZE_IN_MB,
  allowedFileTypes,
  direction = 'row',
}: IDocumentUploadProps) => {
  const intl = useIntl();
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const { enqueueSnackbar } = useSnackbar();

  const form = useForm<IUploadFileForm>({
    defaultValues: { documentType: null },
  });

  const formFields = form.watch();

  const maxFileSize = maxFileSizeInMB * BYTES_IN_MB;
  const allowedTypes = allowedFileTypes || (Object.values(AllowedFileTypesEnum) as string[]);

  const fileValidate = (file: File): File | null => {
    const fileExtension = file.name.split('.').pop() || '';

    if (!allowedTypes.includes(fileExtension)) {
      enqueueSnackbar(
        intl.formatMessage({ id: 'applicationDetails.upload.file.mimeTypeError' }, { fileName: file.name }),
        { variant: 'error' },
      );
      return null;
    }
    if (file.size >= maxFileSize) {
      enqueueSnackbar(
        intl.formatMessage({ id: 'applicationDetails.upload.file.maxSizeError' }, { fileName: file.name }),
        { variant: 'error' },
      );
      return null;
    }
    return file;
  };

  const handleFileChange = () => {
    if (!uploadInputRef.current || !uploadInputRef.current.files?.length) return;
    if (!formFields.documentType) {
      enqueueSnackbar(intl.formatMessage({ id: 'common.error.requiredField' }), { variant: 'error' });
      return;
    }

    const { files } = uploadInputRef.current;
    const validFiles = Object.values(files).map(fileValidate).filter(Boolean) as File[];

    if (validFiles.length) {
      onUpload(validFiles, formFields.documentType);
      form.setValue('documentType', null);
    }

    uploadInputRef.current.value = '';
  };

  const uploadFileOnClickHandler = () => uploadInputRef.current?.click();

  return (
    <Card className={'shadow-sm'} styles={{ body: { padding: '8px 12px' } }}>
      <FormProvider {...form}>
        <form>
          <div className={direction === 'row' ? 'flex gap-3 items-center' : 'flex flex-col gap-3'}>
            <div className={direction === 'row' ? 'flex-1' : 'w-full'}>
              <FormSelect
                name={'documentType'}
                rules={{ required: true }}
                options={documentTypeOptions}
                selectProps={{
                  isOptionEqualToValue: (option: ISelectOption, selectedOptionValue: ISelectOption) =>
                    option.label === selectedOptionValue.label,
                  sx: {
                    '& .MuiInputBase-root': {
                      padding: '0px 8px 12px 0px !important',
                      backgroundColor: '#fff',
                      borderRadius: '6px',
                      height: '36px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#d1d5db',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#9ca3af',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3b82f6',
                    },
                  },
                  disabled: isUploading || isDisabled,
                }}
                selectInputProps={{
                  placeholder: intl.formatMessage({ id: 'placeholders.selectDocument' }),
                  inputProps: {
                    style: {
                      cursor: 'pointer',
                    },
                  },
                }}
                className={'w-full'}
              />
            </div>
            <input hidden ref={uploadInputRef} multiple type='file' id={'file-upload'} onChange={handleFileChange} />
            <LoadingButton
              isLoading={isUploading}
              buttonProps={{
                variant: 'contained',
                className: direction === 'row' ? 'h-9 px-4' : 'h-9 px-4 w-full',
                disabled: !formFields.documentType || isDisabled,
                onClick: uploadFileOnClickHandler,
              }}
              circularProgressProps={{ size: 20 }}
            >
              <span className={'flex items-center gap-2'}>
                <UploadOutlined />
                <FormattedMessage id={'applicationDetails.cta.uploadDocuments'} />
              </span>
            </LoadingButton>
          </div>
        </form>
      </FormProvider>
    </Card>
  );
};
